import React, { useRef, useState } from 'react';
import Select from '@components/Select';
import { FilterStyledPopper } from '@components/Select/constants';
import { Calendar } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Button, ClickAwayListener, Divider, SvgIcon, svgIconClasses, useTheme } from '@mui/material';
import { PendoFeature } from '@utils/pendo/PendoFeature';
import { checkIsNoneDateBucket, getDateBucketFromExpression } from '@utils/utils';
import { iconSize } from '@fabric-ui/utils';
import { DateTruncExpression } from '../../../views/CustomDashboards/types';
import { DATE_BUCKET_OPTIONS, DATE_BUCKET_OPTIONS_ARR } from './constants';

export interface DateOptionsProps {
  onApply: (value: any) => void;
  field: { value: string; expression?: DateTruncExpression };
  selectedFields: Record<string, { expression?: DateTruncExpression }>;
}

function DateOptions({ field, onApply, selectedFields }: DateOptionsProps) {
  const buttonRef = useRef(null);

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const initialValue = field.expression
    ? DATE_BUCKET_OPTIONS[getDateBucketFromExpression(field.expression)].value
    : DATE_BUCKET_OPTIONS.none.value;
  const [selectedValue, setSelectedValue] = useState(initialValue);

  const theme = useTheme();

  const style = {
    color: isPopupOpen ? theme.palette.colors.primary[500] : theme.palette.colors.neutrals[600],
    ...iconSize(16),
  };

  const onClick = event => {
    event.stopPropagation();
    setIsPopupOpen(true);
  };

  const closePopup = event => {
    event.stopPropagation();
    setIsPopupOpen(false);
  };

  const onApplyClick = event => {
    onApply(selectedValue);
    closePopup(event);
  };

  const onChange = value => {
    setSelectedValue(value);
  };

  const shouldDisplayIcon =
    selectedFields &&
    selectedFields[field.value] &&
    selectedFields[field.value].expression &&
    !checkIsNoneDateBucket(getDateBucketFromExpression(selectedFields[field.value].expression!));

  return (
    <Box
      sx={{
        position: 'relative',
        [`:hover .${svgIconClasses.root}`]: {
          color: theme.palette.colors.primary[500],
        },
        [`& button .${svgIconClasses.root}`]: {
          ...((shouldDisplayIcon || isPopupOpen) && { opacity: 1 }),
        },
      }}
      onClick={event => event.stopPropagation()}>
      <Button onClick={onClick} ref={buttonRef} sx={{ position: 'relative' }}>
        <SvgIcon sx={style}>
          <Calendar />
        </SvgIcon>
      </Button>

      <ClickAwayListener onClickAway={closePopup} mouseEvent="onMouseDown" touchEvent="onTouchStart">
        <FilterStyledPopper open={isPopupOpen} anchorEl={buttonRef.current} placement="top-start" sx={{ padding: 0, position: 'relative' }}>
          <Box sx={{ padding: 3 }}>
            <Select
              value={selectedValue}
              options={DATE_BUCKET_OPTIONS_ARR}
              onChange={onChange}
              label="Group Date By"
              size="small"
              style={{ width: '240px' }}
              muiProps={{ disableClearable: true }}
            />
          </Box>

          <Divider />

          <Box sx={{ ...flex.justifyEnd, gap: 1, padding: '8px 16px' }}>
            <Button size="small" onClick={closePopup}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={onApplyClick} data-pendo-id={PendoFeature.TimeBucketingApply}>
              Apply
            </Button>
          </Box>
        </FilterStyledPopper>
      </ClickAwayListener>
    </Box>
  );
}

export default DateOptions;
